import React from 'react';

/**
 * ComsErrorBanner
 * Standardized error display component for ComSapp integration errors.
 * Displays contextual error messages with actionable next steps.
 */
const ComsErrorBanner = ({ code, message, onRetry }) => {
  // Map error codes to user-friendly messages and suggestions
  const errorMap = {
    account_required: {
      title: 'ComSapp Account Not Found',
      detail: 'Your account needs to be created in ComSapp before you can access communications.',
      suggestion: 'Contact your administrator or try creating an account in ComSapp.',
      severity: 'warn',
    },
    api_unavailable: {
      title: 'ComSapp Unavailable',
      detail: 'The communications service is not currently reachable.',
      suggestion: 'This is temporary. Try again in a few moments or contact support.',
      severity: 'error',
    },
    channel_not_configured: {
      title: 'Channel Not Configured',
      detail: 'This communication channel has not been set up by an administrator.',
      suggestion: 'Contact your administrator to configure this channel.',
      severity: 'warn',
    },
    provision_failed: {
      title: 'Provision Error',
      detail: 'An error occurred while setting up your channel access.',
      suggestion: 'Contact your administrator with this error for assistance.',
      severity: 'error',
    },
    no_channels: {
      title: 'No Channels Available',
      detail: 'There are no communication channels available for your role.',
      suggestion: 'Contact your administrator if you believe this is incorrect.',
      severity: 'muted',
    },
    unauthorized: {
      title: 'Access Denied',
      detail: 'You do not have permission to access this communications hub.',
      suggestion: 'Contact your administrator to request access.',
      severity: 'error',
    },
    slug_verification_failed: {
      title: 'Channel Verification Failed',
      detail: 'The channel slug could not be verified against ComSapp.',
      suggestion: 'Check the slug configuration and try verifying again. Ensure ComSapp is accessible.',
      severity: 'error',
    },
  };

  const error = errorMap[code] || {
    title: message || 'Unexpected Error',
    detail: 'An unexpected error occurred. Please try again or contact support.',
    suggestion: '',
    severity: 'error',
  };

  const severityStyle = {
    error: {
      background: '#7f1d1d',
      border: '1px solid #dc2626',
      color: '#fca5a5',
      borderLeft: '4px solid #dc2626',
    },
    warn: {
      background: '#78350f',
      border: '1px solid #f59e0b',
      color: '#fbbf24',
      borderLeft: '4px solid #f59e0b',
    },
    muted: {
      background: '#1e293b',
      border: '1px solid #475569',
      color: '#cbd5e1',
      borderLeft: '4px solid #64748b',
    },
    info: {
      background: '#0c4a6e',
      border: '1px solid #0284c7',
      color: '#93c5fd',
      borderLeft: '4px solid #0284c7',
    },
  };

  const style = severityStyle[error.severity] || severityStyle.error;

  return (
    <div
      style={{
        ...style,
        padding: '14px 16px',
        borderRadius: 6,
        marginBottom: 16,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{error.title}</div>
      <div style={{ opacity: 0.9, fontSize: 12, marginBottom: 8 }}>{error.detail}</div>
      {error.suggestion && (
        <div style={{ opacity: 0.85, fontSize: 12, fontStyle: 'italic', marginBottom: 8 }}>
          💡 {error.suggestion}
        </div>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'inherit',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
            marginTop: 8,
          }}
        >
          Try Again
        </button>
      )}
    </div>
  );
};

export default ComsErrorBanner;
